import { Component, Input } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import {
  CONFIGURATION_PROPAGATION_SECONDS,
  describeConfigurationValue,
  GlobalConfiguration,
  GlobalConfigurationValue,
} from 'src/app/models/GlobalConfiguration';

/**
 * Passo de confirmação de uma chave de cobrança (CU-86akkmfhx, C.4).
 *
 * Mexer numa flag da carteira, numa faixa de saldo, na data de corte ou num
 * preço muda quanto e como os clientes são cobrados. O modal mostra o que está
 * em vigor e o que vai entrar, e só fecha com `true` pelo botão explícito —
 * ESC, clique fora e "Cancelar" dispensam sem salvar.
 *
 * Os rótulos chegam prontos (`describeConfigurationValue`). Quem grava chave de
 * configuração abre o modal por `confirmConfigurationChange` — a tela global e a
 * de bônus, para o passo ser o mesmo nas duas.
 */
@Component({
  selector: 'app-configuration-confirm-modal',
  templateUrl: './configuration-confirm-modal.component.html',
  styleUrls: ['./configuration-confirm-modal.component.scss'],
})
export class ConfigurationConfirmModalComponent {

  @Input() configuration: GlobalConfiguration;
  @Input() currentLabel: string;
  @Input() nextLabel: string;

  readonly propagationSeconds = CONFIGURATION_PROPAGATION_SECONDS;

  constructor(public activeModal: NgbActiveModal) {}

  confirm(): void {
    this.activeModal.close(true);
  }

  cancel(): void {
    this.activeModal.dismiss('CANCEL');
  }
}

/**
 * Abre a confirmação de uma chave de cobrança. Resolve `true` só no botão explícito; ESC,
 * clique fora e "Cancelar" resolvem `false` (sem unhandled rejection).
 *
 * O modal é pendurado no `body`, fora do shell: sem a classe de versão (`themeClass`) os
 * tokens caem no `:root` e o primário fica verde no internacional.
 */
export function confirmConfigurationChange(
  modalService: NgbModal,
  themeClass: string,
  configuration: GlobalConfiguration,
  nextValue: GlobalConfigurationValue,
  /** Rótulos prontos, para valor que o tipo não descreve bem (a tabela de faixas de bônus). */
  labels?: { current: string; next: string }
): Promise<boolean> {
  const modalRef = modalService.open(ConfigurationConfirmModalComponent, {
    backdrop: 'static',
    windowClass: themeClass,
    ariaLabelledBy: 'configuration-confirm-title',
  });

  modalRef.componentInstance.configuration = configuration;
  modalRef.componentInstance.currentLabel = labels?.current ?? describeConfigurationValue(configuration, configuration.value);
  modalRef.componentInstance.nextLabel = labels?.next ?? describeConfigurationValue(configuration, nextValue);

  return modalRef.result.then((confirmed) => confirmed === true, () => false);
}
